'use client'

import * as Dialog from '@radix-ui/react-dialog'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'

const DESKTOP_BREAKPOINT_QUERY = '(min-width: 1280px)'

type MobileMenuChild = { href: string; label: string }
type MobileMenuItem = { href: string; label: string; children?: MobileMenuChild[] }

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`)
}

function getActiveHref(pathname: string, items: MobileMenuItem[]): string | null {
  let activeHref: string | null = null
  let maxMatchLength = 0

  for (const item of items) {
    const isMatch = pathname === item.href || pathname.startsWith(`${item.href}/`)
    if (isMatch && item.href.length > maxMatchLength) {
      activeHref = item.href
      maxMatchLength = item.href.length
    }
  }

  return activeHref
}

export function InventoryHomeMobileMenu({
  items,
  homeHref,
  ctaHref,
  ctaLabel,
  ariaLabel,
  openLabel,
  closeLabel,
}: {
  items: MobileMenuItem[]
  homeHref: string
  ctaHref: string
  ctaLabel: string
  ariaLabel: string
  openLabel: string
  closeLabel: string
}) {
  const pathname = usePathname() ?? ''
  const activeHref = getActiveHref(pathname, items)
  const [open, setOpen] = useState(false)
  const [expandedHref, setExpandedHref] = useState<string | null>(null)

  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_BREAKPOINT_QUERY)
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) setOpen(false)
    }
    handleChange(mql)
    mql.addEventListener('change', handleChange)
    return () => mql.removeEventListener('change', handleChange)
  }, [])

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button
          variant="iconGhost"
          size="icon"
          className="flex h-11 w-11 items-center justify-center xl:hidden"
          aria-label={openLabel}
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </Button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-charcoal-900/60 opacity-0 backdrop-blur-sm transition-opacity duration-200 data-[state=open]:opacity-100" />
        <Dialog.Content
          aria-label={ariaLabel}
          className="fixed inset-0 z-[70] flex -translate-y-2 flex-col bg-white opacity-0 transition-all duration-200 data-[state=open]:translate-y-0 data-[state=open]:opacity-100"
        >
          <div className="flex h-20 items-center justify-between border-b border-gray-200 px-6 lg:px-8">
            <Dialog.Title className="sr-only">{ariaLabel}</Dialog.Title>
            <Dialog.Close asChild>
              <Link href={homeHref} className="flex items-center gap-2">
                <Image
                  src="/assets/dcorp_logo.webp"
                  alt="DCORP R-KEEPER"
                  width={80}
                  height={80}
                  className="h-14 w-14 object-contain"
                />
              </Link>
            </Dialog.Close>
            <Dialog.Close asChild>
              <Button
                variant="iconGhost"
                size="icon"
                className="flex h-11 w-11 items-center justify-center"
                aria-label={closeLabel}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </Button>
            </Dialog.Close>
          </div>

          <nav className="flex flex-1 flex-col overflow-y-auto px-6 py-8 lg:px-8" aria-label={ariaLabel}>
            <ul className="flex flex-col divide-y divide-gray-100">
              {items.map((item) => {
                const active = item.href === activeHref

                if (item.children && item.children.length > 0) {
                  const isExpanded = expandedHref === item.href
                  return (
                    <li key={item.href}>
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        onClick={() => setExpandedHref(isExpanded ? null : item.href)}
                        className={`flex min-h-[44px] w-full items-center justify-between py-3 text-left text-base font-semibold uppercase tracking-wide transition-colors ${
                          active ? 'text-brand-red' : 'text-charcoal-800 hover:text-brand-red'
                        }`}
                      >
                        {item.label}
                        <svg
                          className={`h-4 w-4 flex-shrink-0 text-charcoal-500 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      <div
                        className="grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out"
                        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                      >
                        <div className="min-h-0">
                          <ul className="flex flex-col gap-1 pb-3 pl-4">
                            {item.children.map((child) => {
                              const childActive = isActivePath(pathname, child.href)
                              return (
                                <li key={child.href}>
                                  <Dialog.Close asChild>
                                    <Link
                                      href={child.href}
                                      className={`flex min-h-[44px] items-center text-sm font-medium transition-colors ${
                                        childActive ? 'text-brand-red' : 'text-charcoal-600 hover:text-brand-red'
                                      }`}
                                    >
                                      {child.label}
                                    </Link>
                                  </Dialog.Close>
                                </li>
                              )
                            })}
                          </ul>
                        </div>
                      </div>
                    </li>
                  )
                }

                return (
                  <li key={item.href}>
                    <Dialog.Close asChild>
                      <Link
                        href={item.href}
                        className={`flex min-h-[44px] items-center py-3 text-base font-semibold uppercase tracking-wide transition-colors ${
                          active ? 'text-brand-red' : 'text-charcoal-800 hover:text-brand-red'
                        }`}
                      >
                        {item.label}
                      </Link>
                    </Dialog.Close>
                  </li>
                )
              })}
            </ul>

            <Dialog.Close asChild>
              <Button asChild variant="primary" size="lg" className="mt-8 w-full">
                <Link href={ctaHref}>{ctaLabel}</Link>
              </Button>
            </Dialog.Close>
          </nav>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
